/**
 * Renderiza a árvore de navegação usada tanto no sidebar desktop quanto no
 * drawer mobile. Recebe o estado (aberto/fechado dos submenus) e handlers
 * externamente pra manter Layout.jsx enxuto.
 */
import { Link, useLocation } from "react-router-dom";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import {
  PRIMARY_NAV,
  CADASTROS_NAV,
  OPERATIONAL_NAV,
  REPORTS_NAV,
  FINANCE_NAV,
  FISCAL_NAV,
  ADMIN_NAV,
  SETTINGS_NAV,
  FINANCE_GROUP_ICON,
  CADASTROS_GROUP_ICON,
  SETTINGS_GROUP_ICON,
  FISCAL_GROUP_ICON,
  REPORTS_GROUP_ICON,
} from "./navConfig";

function isActive(pathname, to) {
  if (to === "/") return pathname === "/";
  if (to === "/relatorios") return pathname === "/relatorios";
  return pathname.startsWith(to);
}

function NavLinkItem({ item, showLabels, active, onNavigate }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      className={`${active ? "active" : ""} ${!showLabels ? "!justify-center !px-2" : ""}`}
      data-testid={item.testid}
      title={item.label}
    >
      <Icon size={showLabels ? 18 : 20} weight={active ? "fill" : "regular"} />
      {showLabels && <span>{item.label}</span>}
    </Link>
  );
}

function Submenu({ items, isOpen, setOpen, toggleTestId, icon: TogIcon, label, showLabels, pathname, onNavigate }) {
  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`${!showLabels ? "!justify-center !px-2" : ""}`}
        data-testid={toggleTestId}
        title={label}
      >
        <TogIcon size={showLabels ? 18 : 20} weight="bold" />
        {showLabels && <span className="flex-1">{label}</span>}
        {showLabels && (isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />)}
      </button>
      {isOpen && showLabels && (
        <div className="bg-zinc-50">
          {items.map((n) => {
            const Icon = n.icon;
            const active = isActive(pathname, n.to);
            return (
              <Link key={n.to} to={n.to} onClick={onNavigate} className={`${active ? "active" : ""} !pl-12 text-xs`} data-testid={n.testid}>
                <Icon size={14} weight={active ? "fill" : "regular"} />
                <span>{n.label}</span>
              </Link>
            );
          })}
        </div>
      )}
      {isOpen && !showLabels &&
        items.map((n) => (
          <NavLinkItem key={n.to} item={n} showLabels={false} active={isActive(pathname, n.to)} onNavigate={onNavigate} />
        ))}
    </>
  );
}

export default function SidebarNav({
  hasPerm,
  isAdminReal,
  showLabels,
  cadastrosOpen,
  setCadastrosOpen,
  reportsOpen,
  setReportsOpen,
  financeOpen,
  setFinanceOpen,
  fiscalOpen,
  setFiscalOpen,
  settingsOpen,
  setSettingsOpen,
  onNavigate,
}) {
  const { pathname } = useLocation();

  const visiblePrimary = PRIMARY_NAV.filter((n) => hasPerm(n.perm));
  const visibleCadastros = CADASTROS_NAV.filter((n) => hasPerm(n.perm));
  const visibleOperational = OPERATIONAL_NAV.filter((n) => hasPerm(n.perm));
  const visibleReports = REPORTS_NAV.filter((n) => hasPerm(n.perm));
  const visibleFinance = FINANCE_NAV.filter((n) => hasPerm(n.perm));
  const visibleFiscal = FISCAL_NAV.filter((n) => hasPerm(n.perm));
  const visibleSettings = SETTINGS_NAV.filter((n) => {
    if (n.adminOnly && !isAdminReal) return false;
    if (n.perm) return hasPerm(n.perm);
    return true;
  });

  return (
    <nav className="flex-1 flex flex-col overflow-y-auto min-h-0">
      {visiblePrimary.map((n) => (
        <NavLinkItem key={n.to} item={n} showLabels={showLabels} active={isActive(pathname, n.to)} onNavigate={onNavigate} />
      ))}

      {visibleCadastros.length > 0 && (
        <Submenu
          items={visibleCadastros}
          isOpen={cadastrosOpen}
          setOpen={setCadastrosOpen}
          toggleTestId="nav-cadastros-toggle"
          icon={CADASTROS_GROUP_ICON}
          label="Cadastros"
          showLabels={showLabels}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}

      {visibleOperational.map((n) => (
        <NavLinkItem key={n.to} item={n} showLabels={showLabels} active={isActive(pathname, n.to)} onNavigate={onNavigate} />
      ))}

      {visibleReports.length > 0 && (
        <Submenu
          items={visibleReports}
          isOpen={reportsOpen}
          setOpen={setReportsOpen}
          toggleTestId="nav-reports-toggle"
          icon={REPORTS_GROUP_ICON}
          label="Relatórios"
          showLabels={showLabels}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}

      {visibleFinance.length > 0 && (
        <Submenu
          items={visibleFinance}
          isOpen={financeOpen}
          setOpen={setFinanceOpen}
          toggleTestId="nav-finance-toggle"
          icon={FINANCE_GROUP_ICON}
          label="Financeiro"
          showLabels={showLabels}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}

      {visibleFiscal.length > 0 && (
        <Submenu
          items={visibleFiscal}
          isOpen={fiscalOpen}
          setOpen={setFiscalOpen}
          toggleTestId="nav-fiscal-toggle"
          icon={FISCAL_GROUP_ICON}
          label="Fiscal · NF-e"
          showLabels={showLabels}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}

      {isAdminReal &&
        ADMIN_NAV.map((n) => (
          <NavLinkItem key={n.to} item={n} showLabels={showLabels} active={isActive(pathname, n.to)} onNavigate={onNavigate} />
        ))}

      {visibleSettings.length > 0 && (
        <Submenu
          items={visibleSettings}
          isOpen={settingsOpen}
          setOpen={setSettingsOpen}
          toggleTestId="nav-settings-toggle"
          icon={SETTINGS_GROUP_ICON}
          label="Configurações"
          showLabels={showLabels}
          pathname={pathname}
          onNavigate={onNavigate}
        />
      )}
    </nav>
  );
}
